import { Logger } from "common/utility/logger/logger";
import { Strings } from "common/utility/string/string";
import { BehaviorSubject, Observable, Subject, Subscriber, Subscription, filter, forkJoin, of, take } from "rxjs";
import { Environment } from "src/environments/environment";
import { IStore } from "../interface/istore";
import { Upgrade } from "../upgrade";
import { ConnectionState } from "./connection-state";

export class IndexDB {
    private stores: Array<IStore> = []
    private databaseName: string = Strings.Empty
    private request: IDBOpenDBRequest | undefined
    private connectionState: Subject<ConnectionState> = new BehaviorSubject<ConnectionState>(ConnectionState.Disconnected)
    private factory: IDBFactory
    private closeListener: Subscription | undefined
    private origin: string = "Unknown origin"

    protected database!: IDBDatabase;

    protected get Factory(): IDBFactory {
        return this.factory
    }

    public get ConnectionState(): Subject<ConnectionState> {
        return this.connectionState
    }

    public read(store: IStore, key: any): Observable<any> {
        let returnValue: Observable<any> = of(null)
        this.logConnectionState(`read('${store.Name}')`)

        if (!this.database) return returnValue

        returnValue = new Observable<any>((subscriber: Subscriber<any>) => {
            try {
                const transaction: IDBTransaction = this.createTransaction(store)
                const objectStore: IDBObjectStore = transaction!.objectStore(store.Name)
                const request: IDBRequest | undefined = objectStore?.get(key)

                request.onsuccess = () => subscriber.next(request?.result)
                transaction!.onerror = (event) => subscriber.error(event)
            }
            catch (error) {
                subscriber.error(error)
                subscriber.complete()
            }
        })
        return returnValue
    }

    public all(store: IStore): Observable<any> {
        let returnValue: Observable<any> = of(null)
        this.logConnectionState(`all('${store.Name}')`)

        if (!this.database) return returnValue

        returnValue = new Observable<any>((subscriber: Subscriber<any>) => {
            try {
                const transaction: IDBTransaction = this.createTransaction(store)
                const objectStore: IDBObjectStore = transaction.objectStore(store.Name)
                const request: IDBRequest | undefined = objectStore?.getAll()

                request.onsuccess = () => subscriber.next(request?.result)
                transaction.onerror = (event) => subscriber.error(event)
            }
            catch (error) {
                subscriber.error(error)
                subscriber.complete()
            }
        })
        return returnValue
    }

    public add(store: IStore, item: any): Observable<any> {
        let returnValue: Observable<any> = of(null)
        this.logConnectionState(`add('${store.Name}')`)

        if (!this.database) {
            this.warning("Adding single item to store but Database is not set")
            return returnValue
        }

        returnValue = new Observable<any>((subscriber: Subscriber<any>) => {
            try {
                const transaction: IDBTransaction = this.createTransaction(store, false)
                const objectStore: IDBObjectStore = transaction!.objectStore(store.Name)
                this.debug(`Adding single item to store: '${store.Name}'`, item)
                const request: IDBRequest = objectStore.put(item)

                request.onsuccess = () => subscriber.next(request.result)
                transaction!.onerror = (event) => subscriber.error(event)

            }
            catch (error) {
                this.error(`Adding single item to store: '${store.Name}'`, false, error)
                subscriber.error(error)
                subscriber.complete()
            }
        })
        return returnValue
    }

    public addAll(store: IStore, items: Array<any>): Observable<any> {
        let returnValue: Observable<any> = of(null)
        this.logConnectionState(`addAll('${store.Name}')`)

        returnValue = new Observable<void>((subscriber: Subscriber<void>) => {
            try {
                const transaction: IDBTransaction = this.createTransaction(store, false)
                const objectStore: IDBObjectStore = transaction!.objectStore(store.Name)
                const requests: Array<Observable<void>> = []

                items.forEach(item => {
                    requests.push(new Observable<void>((subscriber: Subscriber<void>) => {
                        const request: IDBRequest = objectStore.put(item)
                        request.onsuccess = () => {
                            this.debug("Added item to store", item)
                            subscriber.next()
                            subscriber.complete()
                        }
                    }))
                })

                forkJoin(requests)
                    .subscribe(() => {
                        this.debug("All items added to store:")
                        transaction!.commit()
                        subscriber.next()
                    })
            }
            catch (error) {
                this.error(`Adding ${items.length} items to store: '${store.Name}'`, false, error)
                subscriber.error(error)
                subscriber.complete()
            }
        })
        return returnValue
    }

    public close(): void {
        this.closeListener?.unsubscribe()
        this.database?.close()
    }

    public createTransaction(store: IStore, readOnly: boolean = true): IDBTransaction {
        const mode: IDBTransactionMode = readOnly ? "readonly" : "readwrite"
        this.trace(`Creating '${mode}' transaction for store: '${store.Name}'`)
        return this.database.transaction(store.Name, mode)
    }

    constructor(indexDb: IDBFactory, databaseName: string | undefined = undefined, stores: Array<IStore>, origin: string | undefined = undefined) {
        this.origin = origin ?? this.origin
        this.factory = indexDb
        this.stores = stores ?? []
        this.databaseName = databaseName ?? Environment.DatabaseName
        this.initialize()
    }
    
    protected initialize(): void {
        this.ConnectionState.next(ConnectionState.Connecting)
        this.debug(`Opening Database: '${this.databaseName}'`)
        this.request = this.Factory.open(this.databaseName, Environment.DatabaseVersion)
        this.handleEvents(this.request)
        this.closeListener = this.keepDatabaseOpen()
    }

    protected createObjectStores(versionChange: IDBVersionChangeEvent): void {
        this.connectionState.next(ConnectionState.Upgrading)
        
        const request: IDBOpenDBRequest = versionChange.target as IDBOpenDBRequest
        request.onsuccess = () => this.connectionState.next(ConnectionState.Connected)
        
        if (!this.database) {
            if (!request.result) {
                return
            }
            this.assignDatabase(request.result)
            this.debug(`onupgradeneeded triggered. Creating ${this.stores.length} Object stores for database: '${request.result?.name}'`)
        }
        const upgrade: Upgrade = new Upgrade(this.database, versionChange, this.stores)
        
        if (!upgrade.createStores()){
            Logger.error(`Failed to create stores between database versions: ${versionChange.oldVersion} - ${versionChange.newVersion ?? 'unknown'}`)
        }

        this.debug(`Database logging`, this.database)
    }

    protected reset(reInitialize: boolean = true): void {
        this.database?.close();
        this.delete()
        reInitialize ? this.initialize() : null
        this.debug(`Reset Database '${this.databaseName}'`)
    }

    protected delete(): void {
        this.debug(`Deleting Database '${this.databaseName}'`)
        this.Factory.deleteDatabase(this.databaseName);
    }

    private keepDatabaseOpen(): Subscription {
        return this.ConnectionState.pipe(
            filter(state => state === ConnectionState.Disconnected)
        ).subscribe(() => {
            this.debug("Database Closed")
            this.initialize()
        })
    }

    private handleEvents(request: IDBOpenDBRequest): void {
        request.onsuccess = (event: Event) => this.setDatabase(event)
        request.onupgradeneeded = (versionChange: IDBVersionChangeEvent) => this.createObjectStores(versionChange)
        request.onerror = (event: Event) => this.initializationError(event)
        request.onblocked = (event: Event) => this.initializationBlocked(event)
    }

    private initializationError(event: Event): void {
        this.error("Error creating database", false, event)
    }

    private initializationBlocked(event: Event): void {
        this.error("Error creating database. Opening blocked by version change", false, event)
    }

    private setDatabase(event: Event): void {
        const request: IDBOpenDBRequest | undefined = event?.target as IDBOpenDBRequest | undefined
        if (!!request && !this.database) {
            this.assignDatabase(request.result)
            Logger.debug(`Opened Database: '${request.result?.name}' version: ${request.result?.version}`)
            this.connectionState.next(ConnectionState.Connected)
            this.closeListener = this.keepDatabaseOpen()
        }
        this.debug(`Opened Database: '${this.database?.name}' version: ${this.database?.version}`)
        this.connectionState.next(ConnectionState.Connected)
    }

    private assignDatabase(database: IDBDatabase): void {
        this.database = database
        this.database.onclose = () => this.connectionState.next(ConnectionState.Disconnected)
    }

    private logConnectionState(message: string = "Unknown Source"): void {
        this.ConnectionState.pipe(
            take(1)
        ).subscribe((state: ConnectionState) => {
            this.trace(`${message}: Connection State: ${ConnectionState[state]}`)
        })
    }

    private debug(message: any, ...params: any): void {
        Logger.debug(this.prefixMessage(message), ...params)
    }

    private trace(message: any, ...params: any): void {
        Logger.trace(this.prefixMessage(message), ...params)
    }

    private error(message: any, sendRemote: boolean = true, ...params: any): void {
        Logger.error(this.prefixMessage(message), sendRemote, ...params)
    }

    private warning(message: any, ...params: any): void {
        Logger.warning(this.prefixMessage(message), ...params)
    }

    private prefixMessage(message: string): string {
        return `${this.origin}: ${message}`
    }

}
